import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from 'src/environments/environment';

/**
 * The tender methods this terminal accepts — "how was this paid?" (daily reconciliation Stage 2).
 *
 * Fetched once and held, like the currency context: it is a short settings list read on every payment
 * screen, and re-fetching it per dialog would be a request per truck at the gate.
 */
@Injectable({ providedIn: 'root' })
export class TenderMethodService {
  currentDomain = environment.currentApiHostname;
  apiUrl = `https://${this.currentDomain}/api`;

  /** Empty until loaded. A picker renders nothing rather than guessing a list. */
  public methods: Array<string> = [];

  /**
   * GROUP => the methods inside it, as the terminal configured them. One question first ("cash,
   * swiped, mobile money?"), then which wallet — a group of one is just a button.
   */
  public groups: Record<string, Array<string>> = {};
  private loading = false;

  constructor(private http: HttpClient) {}

  /**
   * The logo files for a method, by the name the terminal configured. Two for the card rail; none for
   * cash or a transfer, which have no logo to use — the picker falls back to an icon.
   *
   * Spellings are normalised the way Atenza's own resolver does it, because the fund's records write
   * ZB's wallet four different ways ("ZB", "ZB WALLET", "SMILECASH", "ZB SMILE CASH") and they are all
   * one logo.
   */
  logosFor(method: string): Array<string> {
    const code = (method ?? '').trim().toUpperCase().replace(/[\s-]+/g, '_');
    const dir = 'assets/images/wallets/';

    switch (code) {
      case 'ECOCASH':
      case 'PESEPAY':
        return [dir + 'ecocash.png'];
      case 'ONE_MONEY':
      case 'ONEMONEY':
        return [dir + 'onemoney.png'];
      case 'OMARI':
        return [dir + 'omari.png'];
      case 'INNBUCKS':
        return [dir + 'innbucks.png'];
      case 'ZB':
      case 'ZB_WALLET':
      case 'SMILECASH':
      case 'ZB_SMILE_CASH':
      case 'ZB_SMILECASH':
        return [dir + 'zb.png'];
      case 'VISA':
        return [dir + 'visa.png'];
      case 'MASTERCARD':
      case 'MASTER_CARD':
        return [dir + 'mastercard.png'];
      case 'ZIMSWITCH':
      case 'ZIM_SWITCH':
        return [dir + 'zimswitch.png'];
      case 'SWIPE':
      case 'CARD':
        // The GROUP, when the terminal has not split it into schemes: both marks, as before.
        return [dir + 'visa.png', dir + 'mastercard.png'];
      default:
        return [];
    }
  }

  /** The bootstrap icon for a method with no logo of its own. */
  iconFor(method: string): string {
    const code = (method ?? '').trim().toUpperCase();

    if (code === 'CASH') {
      return 'bi-cash-stack';
    }
    if (code === 'EFT' || code === 'TRANSFER' || code === 'RTGS') {
      return 'bi-bank';
    }

    return 'bi-wallet2';
  }

  /** Load once. Safe to call from every picker's ngOnInit. */
  load(): void {
    if (this.loading || this.methods.length > 0) {
      return;
    }
    this.loading = true;

    this.http
      .get<{ groups: Record<string, Array<string>>; methods: Array<string> }>(`${this.apiUrl}/payment-methods`, {
        headers: new HttpHeaders({
          Accept: 'application/json',
          Authorization: 'Bearer ' + localStorage.getItem('access_token'),
        }),
      })
      .subscribe({
        next: (response) => {
          this.groups = response?.groups ?? {};
          this.methods = response?.methods ?? [];
          this.loading = false;
        },
        // A failed load leaves the list empty, so the picker shows nothing and the operator is not
        // offered a method the API would refuse. Never a hard-coded fallback: the list is the
        // terminal's own setting, and guessing it is how money lands in a bucket nobody configured.
        error: () => {
          this.groups = {};
          this.methods = [];
          this.loading = false;
        },
      });
  }
}
